import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useGlobal } from "@/context/GlobalContext";
import { useHomeRoute } from "@/hooks/useHomeRoute";
import { useTheme } from "@/context/ThemeContext";
import { MenuIcon } from "@/lib/menuIcons";
import { StructureIcon } from "@/components/StructureIcon";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ChevronDown, ChevronsLeft, ChevronsRight, Network, Loader2, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export default function Sidebar({ onOpenTree, mobile = false, onNavigate, treePinned = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const homeRoute = useHomeRoute();
  const { structures, structureKey, setStructureKey, currentStructure } = useGlobal();
  const { theme } = useTheme();
  const [collapsedState, setCollapsedState] = useState(() => localStorage.getItem("zae_sidebar_collapsed") === "1");
  const collapsed = mobile ? false : collapsedState;
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("zae_open_groups")) || { informe_resultados: true }; }
    catch { return { informe_resultados: true }; }
  });

  const { data: selColors } = useQuery({
    queryKey: ["sel-colors"],
    queryFn: async () => (await api.get("/params/structure-colors")).data,
  });
  const selColor = selColors?.[structureKey]?.[theme] || currentStructure?.color || "#DC2626";

  const { data: menu, isLoading } = useQuery({
    queryKey: ["menu"],
    queryFn: async () => (await api.get("/menu")).data,
  });

  const toggleCollapse = () => {
    setCollapsedState((c) => { localStorage.setItem("zae_sidebar_collapsed", c ? "0" : "1"); return !c; });
  };
  const toggleGroup = (id) => setOpenGroups((o) => {
    const n = { ...o, [id]: !o[id] };
    localStorage.setItem("zae_open_groups", JSON.stringify(n));
    return n;
  });

  // Mantener abierto el grupo que contiene la ruta activa (evita que los reportes "desaparezcan" al navegar)
  useEffect(() => {
    if (!menu) return;
    const grp = menu.find((g) => g.type === "group" && g.children?.some((c) => c.route === location.pathname || c.children?.some((s) => s.route === location.pathname)));
    const sub = grp?.children?.find((c) => c.children?.some((s) => s.route === location.pathname));
    if (grp) setOpenGroups((o) => ((o[grp.id] && (!sub || o[sub.id])) ? o : { ...o, [grp.id]: true, ...(sub ? { [sub.id]: true } : {}) }));
  }, [menu, location.pathname]);

  const linkClass = (active) =>
    `flex items-center gap-3 rounded-lg text-sm transition-colors ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"} ${
      active ? "text-white font-semibold" : "text-slate-400 hover:text-white hover:bg-white/5"
    }`;

  return (
    <aside
      className={`${collapsed ? "w-16" : "w-72"} shrink-0 bg-[#0B1220] border-r border-[#1c2740] ${mobile ? "h-full" : "sticky top-16 h-[calc(100vh-4rem)]"} flex flex-col transition-all duration-200 z-30`}
      data-testid="sidebar"
    >
      {/* Toggle */}
      <div className={`flex items-center ${collapsed ? "justify-center" : "justify-between"} px-3 h-12 border-b border-[#1c2740]`}>
        {!collapsed && <span className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Menú</span>}
        {mobile ? (
          <button onClick={onNavigate} data-testid="sidebar-close-mobile" title="Cerrar"
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5">
            <X className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={toggleCollapse} data-testid="sidebar-toggle" title={collapsed ? "Expandir" : "Retraer"}
            className="w-8 h-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-white hover:bg-white/5">
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
        )}
      </div>

      {/* Menú dinámico */}
      <nav className="flex-1 overflow-y-auto p-2 space-y-1" data-testid="sidebar-nav">
        {/* Estructura Organizacional (dentro del menú) */}
        <button type="button" title="Estructura Organizacional" data-testid="menu-item-estructura_organizacional" onClick={() => { onNavigate?.(); navigate(homeRoute()); }}
          className={`w-full flex items-center gap-3 rounded-lg text-sm transition-colors ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"} ${location.pathname === "/estructura" ? "font-semibold text-white" : "text-slate-200 hover:text-white hover:bg-white/5"}`}
          style={location.pathname === "/estructura" ? { backgroundColor: selColor } : undefined}>
          <StructureIcon name={currentStructure?.icon} className="w-5 h-5 shrink-0" style={{ color: location.pathname === "/estructura" ? "#ffffff" : (currentStructure?.color || "#DC2626") }} />
          {!collapsed && <span className="whitespace-nowrap font-semibold">Estructura Organizacional</span>}
        </button>

        {!collapsed && (
          <div className="pl-2 border-l border-white/15 ml-5 space-y-1.5 pb-1">
            <Select value={structureKey || ""} onValueChange={(v) => { setStructureKey(v); onNavigate?.(); }}>
              <SelectTrigger className="h-9 bg-[#131C2E] border-[#223048] text-white text-xs" data-testid="sidebar-structure-select">
                <div className="flex items-center gap-2 min-w-0">
                  {currentStructure && <StructureIcon name={currentStructure.icon} className="w-3.5 h-3.5 shrink-0" style={{ color: currentStructure.color }} />}
                  <SelectValue placeholder="Selecciona estructura" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-[#131C2E] border-[#223048] text-slate-200">
                {structures.map((s) => (
                  <SelectItem key={s.key} value={s.key} data-testid={`sidebar-structure-opt-${s.key}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!treePinned && (
              <button onClick={onOpenTree} data-testid="open-structure-tree"
                className="w-full flex items-center gap-2 pl-3 pr-2 py-2 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5 transition-colors">
                <Network className="w-4 h-4 shrink-0" /> Navegar organización
              </button>
            )}
          </div>
        )}
        {collapsed && (
          <button onClick={toggleCollapse} title={currentStructure?.name || "Estructura"} data-testid="structure-collapsed-btn"
            className="mx-auto w-10 h-10 rounded-lg flex items-center justify-center text-white"
            style={{ backgroundColor: currentStructure?.color || "#DC2626" }}>
            <StructureIcon name={currentStructure?.icon} className="w-5 h-5" />
          </button>
        )}

        <div className="h-px bg-white/10 my-2" />

        {isLoading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-red-600 animate-spin" /></div>
        ) : (
          menu?.filter((item) => item.id !== "estructura_organizacional").map((item) => {
            if (item.type === "group") {
              const hasActive = item.children?.some((c) => location.pathname === c.route);
              const open = collapsed ? true : (openGroups[item.id] ?? false);
              return (
                <div key={item.id}>
                  <button
                    onClick={() => (collapsed ? toggleCollapse() : toggleGroup(item.id))}
                    data-testid={`sidebar-group-${item.id}`}
                    title={item.name}
                    className={`w-full flex items-center gap-3 rounded-lg text-sm transition-colors ${collapsed ? "justify-center px-0 py-2.5" : "px-3 py-2.5"} ${hasActive ? "text-white" : "text-slate-300 hover:text-white hover:bg-white/5"}`}
                  >
                    <MenuIcon name={item.icon} className="w-5 h-5 shrink-0" />
                    {!collapsed && <span className="flex-1 text-left font-semibold truncate">{item.name}</span>}
                    {!collapsed && <ChevronDown className={`w-4 h-4 transition-transform ${openGroups[item.id] ? "" : "-rotate-90"}`} />}
                  </button>
                  <AnimatePresence initial={false}>
                    {!collapsed && openGroups[item.id] && (
                      <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.18 }} className="overflow-hidden">
                        <div className="mt-0.5 space-y-0.5 pl-2 border-l border-[#223048] ml-4">
                          {item.children.map((c) => (
                            c.children && c.children.length ? (
                              <div key={c.id}>
                                <button onClick={() => toggleGroup(c.id)} data-testid={`sidebar-subgroup-${c.id}`}
                                  className="w-full flex items-center gap-2.5 pl-3 pr-2 py-2 rounded-lg text-sm text-slate-300 hover:text-white hover:bg-white/5">
                                  <MenuIcon name={c.icon} className="w-4 h-4 shrink-0" />
                                  <span className="flex-1 text-left leading-tight truncate">{c.name}</span>
                                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openGroups[c.id] ? "" : "-rotate-90"}`} />
                                </button>
                                {openGroups[c.id] && (
                                  <div className="mt-0.5 space-y-0.5 pl-2 border-l border-[#223048] ml-4">
                                    {c.children.map((g) => (
                                      <NavLink key={g.id} to={g.route} data-testid={`menu-item-${g.id}`} onClick={onNavigate}
                                        className={({ isActive }) => `flex items-start gap-2.5 pl-3 pr-2 py-2 rounded-lg text-sm transition-colors ${isActive ? "text-white font-semibold" : "text-slate-400 hover:text-white hover:bg-white/5"}`}
                                        style={({ isActive }) => (isActive ? { backgroundColor: selColor } : undefined)}>
                                        <MenuIcon name={g.icon} className="w-4 h-4 shrink-0 mt-0.5" />
                                        <span className="leading-tight">{g.name}</span>
                                      </NavLink>
                                    ))}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <NavLink key={c.id} to={c.route} data-testid={`menu-item-${c.id}`} onClick={onNavigate}
                                className={({ isActive }) => `flex items-start gap-2.5 pl-3 pr-2 py-2 rounded-lg text-sm transition-colors ${isActive ? "text-white font-semibold" : "text-slate-400 hover:text-white hover:bg-white/5"}`}
                                style={({ isActive }) => (isActive ? { backgroundColor: selColor } : undefined)}>
                                <MenuIcon name={c.icon} className="w-4 h-4 shrink-0 mt-0.5" />
                                <span className="leading-tight">{c.name}</span>
                              </NavLink>
                            )
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }
            return (
              <NavLink key={item.id} to={item.route} title={item.name} data-testid={`menu-item-${item.id}`} onClick={onNavigate}
                className={({ isActive }) => linkClass(isActive)}
                style={({ isActive }) => (isActive ? { backgroundColor: selColor } : undefined)}>
                <MenuIcon name={item.icon} className="w-5 h-5 shrink-0" />
                {!collapsed && <span className="truncate">{item.name}</span>}
              </NavLink>
            );
          })
        )}
      </nav>
    </aside>
  );
}
